import { AbstractControl, ValidationErrors } from '@angular/forms';
import { GENERIC_ASSISTANT_CONFIGURATION_FILENAME } from '@shared';
import type { AssistantBuilderTraitGenericFilesValue } from './assistant-builder-trait-generic-form.validators';

/**
 * Case-insensitive filename normalization (QA#2): used to DETECT a skill.md —
 * both a near-miss in the config control (`Skill.MD`) and a misplaced skill.md
 * among source files. Acceptance stays exact-match: the server contract
 * (`assertSkillMdAssistantDocumentFilenames`, PR-2) requires the exact
 * `skill.md` name, and the UI must not accept a file the API will reject
 * (Stage 3 plan, open question 4 standing default).
 */
const normalizeFilename = (filename: string): string =>
  filename.trim().toLowerCase();

const isExactSkillMd = (file: { name: string }): boolean =>
  file.name === GENERIC_ASSISTANT_CONFIGURATION_FILENAME;

const isSkillMdLike = (file: { name: string }): boolean =>
  normalizeFilename(file.name) === GENERIC_ASSISTANT_CONFIGURATION_FILENAME;

/**
 * Group-validator factory for the skill.md configuration-file control shared
 * by the generic, comparison and compliance trait forms (SB-3325 T4).
 *
 * Valid ⇔ the config control holds exactly one file named exactly `skill.md`,
 * and no skill.md-like file sits in any of the other file controls.
 *
 * Errors (distinct so getFormErrorMessage can tell them apart):
 * - `{ skillMdConfigurationFile: true }` — missing / not-exactly-one config
 *   file, or a skill.md misplaced among the other controls' files;
 * - `{ skillMdFilenameNearMiss: { actual } }` — exactly one config file whose
 *   name is not `skill.md` (including case variants like `Skill.MD`).
 */
export function skillMdConfigurationFileValidator(
  configControlName: string,
  otherFilesControlNames: string[] = [],
): (group: AbstractControl) => ValidationErrors | null {
  return (group: AbstractControl): ValidationErrors | null => {
    const configurationFiles =
      (group.get(configControlName)?.value as
        | AssistantBuilderTraitGenericFilesValue
        | undefined) ?? [];
    const otherFiles = otherFilesControlNames.flatMap(
      (controlName) =>
        (group.get(controlName)?.value as
          | AssistantBuilderTraitGenericFilesValue
          | undefined) ?? [],
    );

    const hasSkillMdInOtherControls = otherFiles.some(isSkillMdLike);
    if (configurationFiles.length !== 1 || hasSkillMdInOtherControls) {
      return { skillMdConfigurationFile: true };
    }

    const configurationFile = configurationFiles[0];
    if (!configurationFile || !isExactSkillMd(configurationFile)) {
      return {
        skillMdFilenameNearMiss: { actual: configurationFile?.name ?? '' },
      };
    }

    return null;
  };
}
